/**
 * Route guard — renders children only when the user is authenticated.
 * On mount, if `ready` and `!isAuthenticated`, kicks off the PKCE redirect
 * with `returnTo` set to the current pathname. The OIDC callback page
 * navigates back to the saved path post-login.
 */
import { Loader2 } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';

import { useAuth } from './AuthContext.js';

export function AuthGuard({ children }: { children: ReactNode }) {
  const { ready, isAuthenticated, signIn } = useAuth();

  useEffect(() => {
    if (ready && !isAuthenticated) {
      const returnTo = window.location.pathname + window.location.search;
      void signIn(returnTo);
    }
  }, [ready, isAuthenticated, signIn]);

  if (!ready || !isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {ready ? 'Redirecting to sign in…' : 'Loading…'}
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
